/**
 * Composition de l'équipe (espace compétition) — identité, postes, membres.
 *
 * Actions disponibles selon le rôle :
 * - Tout membre : démissionner.
 * - Lieutenant : démissionner de sa fonction (reste membre de l'équipe).
 * - Capitaine/lieutenant : évincer un membre, modifier la composition (postes).
 * - Capitaine seul : modifier nom/devise, désigner/retirer le lieutenant,
 *   transférer le capitanat.
 */

import { useState, useEffect } from 'react';
import { PlayerAvatar } from '../../components/PlayerAvatar';
import { useProfile } from '../../contexts/ProfileContext';
import MemberSelect from '../../components/competitions/MemberSelect';
import {
  updateTeam, leaveTeam, expelMember, setLieutenant, transferCaptain, setRoles,
  type MyTeam,
} from '../../lib/teamsApi';
import { EditIcon, SaveIcon, CloseIcon, StarIcon, AdminShieldIcon, LogoutIcon } from '../../components/Icons';
import { BACKEND_URL } from '../../config';
import type { Session } from '../../types/app';

const SLOT_LABELS: Record<string, string> = {
  attaquant: 'Attaquant',
  def_E: 'Défenseur Est',
  def_N: 'Défenseur Nord',
  def_O: 'Défenseur Ouest',
  rempl_1: 'Remplaçant 1',
  rempl_2: 'Remplaçant 2',
  rempl_3: 'Remplaçant 3',
  rempl_4: 'Remplaçant 4',
};
const SLOT_ORDER = ['attaquant', 'def_E', 'def_N', 'def_O', 'rempl_1', 'rempl_2', 'rempl_3', 'rempl_4'];

const ROLE_TAG_COLOR: Record<string, string> = { capitaine: '#e0a020', lieutenant: '#8aa0b8' };
const ROLE_TAG_LABEL: Record<string, string> = { capitaine: 'C', lieutenant: 'L' };

interface TeamCompositionViewProps {
  session: Session;
  team: MyTeam;
  onChanged: () => void;
  onLeft: () => void;
}

export default function TeamCompositionView({ session, team, onChanged, onLeft }: TeamCompositionViewProps) {
  const { openPlayerProfile } = useProfile();
  const token = session.token!;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editingInfo, setEditingInfo] = useState(false);
  const [nom, setNom] = useState(team.nom);
  const [devise, setDevise] = useState(team.devise || '');

  const [editingSlots, setEditingSlots] = useState(false);
  const [slotDraft, setSlotDraft] = useState(team.slots);
  useEffect(() => { setSlotDraft(team.slots); setNom(team.nom); setDevise(team.devise || ''); }, [team]);

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      onChanged();
    } catch {
      setError('Action impossible');
    } finally {
      setBusy(false);
    }
  };

  const members = team.members;

  // Un joueur ne peut occuper qu'un seul poste de jeu : en choisir un déjà
  // affecté ailleurs le retire de son ancien poste (comportement "swap").
  const assignSlot = (slot: string, pseudo: string) => {
    setSlotDraft((prev) => {
      const next = { ...prev, [slot]: pseudo };
      if (pseudo) {
        SLOT_ORDER.forEach((s) => {
          if (s !== slot && next[s as keyof typeof next] === pseudo) {
            (next as Record<string, string>)[s] = '';
          }
        });
      }
      return next;
    });
  };

  return (
    <div className="tm-detail">
      {error && <div className="compet-error">{error}</div>}

      {/* En-tête équipe */}
      <section>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <PlayerAvatar
            pseudo={team.nom}
            size={56}
            photoUrl={`${BACKEND_URL}/api/avatars/team/${encodeURIComponent(team.nom)}?size=112`}
          />
          {!editingInfo ? (
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h2 style={{ margin: 0 }}>{team.nom}</h2>
                {team.is_captain && (
                  <button className="cia-btn cia-btn--icon" title="Modifier le nom / la devise" onClick={() => setEditingInfo(true)}>
                    <EditIcon size={15} />
                  </button>
                )}
              </div>
              {team.devise && <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: 2 }}>« {team.devise} »</div>}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, maxWidth: 340 }}>
              <div className="tm-field">
                <span>Nom de l'équipe</span>
                <input type="text" value={nom} onChange={(e) => setNom(e.target.value)} />
              </div>
              <div className="tm-field">
                <span>Devise (optionnel)</span>
                <input type="text" value={devise} onChange={(e) => setDevise(e.target.value)} />
              </div>
              <div className="tm-toolbar">
                <button className="cia-btn cia-btn--ghost cia-btn--with-icon" disabled={busy} onClick={() => setEditingInfo(false)}>
                  <CloseIcon size={14} /> Annuler
                </button>
                <button
                  className="cia-btn cia-btn--primary cia-btn--with-icon"
                  disabled={busy || !nom.trim()}
                  onClick={() => run(async () => {
                    await updateTeam(token, team.id, { nom: nom.trim(), devise: devise.trim() });
                    setEditingInfo(false);
                  })}
                >
                  <SaveIcon size={14} /> Enregistrer
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Encadrement : capitaine et lieutenant ne sont pas des postes de jeu,
          on les affiche à part (leur désignation se fait depuis la liste des membres) */}
      <section>
        <h3 style={{ margin: '0 0 10px' }}>Encadrement</h3>
        <div className="tm-encadrement-row">
          <div className="tm-encadrement-item">
            <span className="tm-slot-label"><StarIcon size={13} /> Capitaine</span>
            <div className="tm-slot-value">
              {team.slots.capitaine ? (
                <>
                  <PlayerAvatar pseudo={team.slots.capitaine} size={26} />
                  <button className="tm-slot-value-name" onClick={() => openPlayerProfile(team.slots.capitaine)}>
                    {team.slots.capitaine}
                  </button>
                </>
              ) : <span className="tm-slot-value-empty">—</span>}
            </div>
          </div>
          <div className="tm-encadrement-item">
            <span className="tm-slot-label"><AdminShieldIcon size={13} /> Lieutenant</span>
            <div className="tm-slot-value">
              {team.slots.lieutenant ? (
                <>
                  <PlayerAvatar pseudo={team.slots.lieutenant} size={26} />
                  <button className="tm-slot-value-name" onClick={() => openPlayerProfile(team.slots.lieutenant)}>
                    {team.slots.lieutenant}
                  </button>
                </>
              ) : <span className="tm-slot-value-empty">Aucun {team.is_captain ? '— à désigner ci-dessous' : ''}</span>}
            </div>
          </div>
        </div>
      </section>

      {/* Composition (postes de jeu) */}
      <section>
        <div className="tm-section-header">
          <h3>Postes de jeu</h3>
          {team.is_staff && !editingSlots && (
            <button className="cia-btn cia-btn--with-icon" onClick={() => setEditingSlots(true)}>
              <EditIcon size={14} /> Modifier
            </button>
          )}
        </div>

        <div className="tc-teams-grid">
          {SLOT_ORDER.map((slot) => (
            <div key={slot} className="tm-slot-card">
              <span className="tm-slot-label">{SLOT_LABELS[slot]}</span>
              {editingSlots ? (
                <MemberSelect
                  value={slotDraft[slot as keyof typeof slotDraft] || ''}
                  onChange={(pseudo) => assignSlot(slot, pseudo)}
                  options={members}
                  emptyLabel="Vacant"
                />
              ) : (
                <div className="tm-slot-value">
                  {team.slots[slot as keyof typeof team.slots] ? (
                    <>
                      <PlayerAvatar pseudo={team.slots[slot as keyof typeof team.slots] as string} size={26} />
                      <span className="tm-slot-value-name" style={{ cursor: 'default' }}>
                        {team.slots[slot as keyof typeof team.slots] as string}
                      </span>
                    </>
                  ) : <span className="tm-slot-value-empty">Vacant</span>}
                </div>
              )}
            </div>
          ))}
        </div>

        {editingSlots && (
          <div className="tm-toolbar" style={{ marginTop: 12 }}>
            <button
              className="cia-btn cia-btn--ghost cia-btn--with-icon"
              disabled={busy}
              onClick={() => { setSlotDraft(team.slots); setEditingSlots(false); }}
            >
              <CloseIcon size={14} /> Annuler
            </button>
            <button
              className="cia-btn cia-btn--primary cia-btn--with-icon"
              disabled={busy}
              onClick={() => run(async () => {
                await setRoles(token, team.id, {
                  attaquant: slotDraft.attaquant, def_E: slotDraft.def_E, def_N: slotDraft.def_N, def_O: slotDraft.def_O,
                  rempl_1: slotDraft.rempl_1, rempl_2: slotDraft.rempl_2, rempl_3: slotDraft.rempl_3, rempl_4: slotDraft.rempl_4,
                });
                setEditingSlots(false);
              })}
            >
              <SaveIcon size={14} /> Enregistrer la composition
            </button>
          </div>
        )}
      </section>

      {/* Membres / actions */}
      <section>
        <h3 style={{ margin: '0 0 10px' }}>Membres ({members.length})</h3>
        {members.map((m) => (
          <div key={m.pseudo} className="tm-member-row">
            <PlayerAvatar pseudo={m.pseudo} size={38} />
            <div className="competition-card-text" style={{ flex: 1, minWidth: 0 }}>
              <button
                className="competition-card-title"
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
                onClick={() => openPlayerProfile(m.pseudo)}
              >
                {m.pseudo_display || m.pseudo}
              </button>
              <span className="tm-member-row-role">
                {(m.role === 'capitaine' || m.role === 'lieutenant') && (
                  <span className="tm-member-role-tag" style={{ background: ROLE_TAG_COLOR[m.role] }}>
                    {ROLE_TAG_LABEL[m.role]}
                  </span>
                )}
                {m.role}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              {team.is_captain && m.role !== 'capitaine' && (
                <button
                  className="cia-btn cia-btn--small cia-btn--with-icon"
                  disabled={busy}
                  title="Nommer capitaine"
                  onClick={() => {
                    if (window.confirm(`Transférer le capitanat à ${m.pseudo_display || m.pseudo} ?`)) {
                      run(() => transferCaptain(token, team.id, m.pseudo));
                    }
                  }}
                >
                  <StarIcon size={13} /> Capitaine
                </button>
              )}
              {team.is_captain && m.role !== 'capitaine' && (
                m.role === 'lieutenant' ? (
                  <button
                    className="cia-btn cia-btn--small cia-btn--with-icon"
                    disabled={busy}
                    title="Retirer le rôle de lieutenant"
                    onClick={() => run(() => setLieutenant(token, team.id, null))}
                  >
                    <AdminShieldIcon size={13} /> Retirer lieutenant
                  </button>
                ) : (
                  <button
                    className="cia-btn cia-btn--small cia-btn--with-icon"
                    disabled={busy}
                    title="Nommer lieutenant"
                    onClick={() => run(() => setLieutenant(token, team.id, m.pseudo))}
                  >
                    <AdminShieldIcon size={13} /> Lieutenant
                  </button>
                )
              )}
              {/* Le lieutenant peut démissionner de sa fonction lui-même (sans quitter l'équipe),
                  même s'il n'est pas capitaine : l'action ci-dessus est réservée au capitaine. */}
              {!team.is_captain && m.role === 'lieutenant' && m.pseudo.toLowerCase() === session.pseudo.toLowerCase() && (
                <button
                  className="cia-btn cia-btn--small cia-btn--with-icon"
                  disabled={busy}
                  title="Démissionner de la fonction de lieutenant (reste membre de l'équipe)"
                  onClick={() => {
                    if (window.confirm('Démissionner de votre fonction de lieutenant ? Vous resterez membre de l\'équipe.')) {
                      run(() => setLieutenant(token, team.id, null));
                    }
                  }}
                >
                  <AdminShieldIcon size={13} /> Démissionner (lieutenant)
                </button>
              )}
              {team.is_staff && m.role !== 'capitaine' && m.pseudo.toLowerCase() !== session.pseudo.toLowerCase() && (
                <button
                  className="cia-btn cia-btn--small cia-btn--danger"
                  disabled={busy}
                  onClick={() => {
                    if (window.confirm(`Évincer ${m.pseudo_display || m.pseudo} de l'équipe ?`)) {
                      run(() => expelMember(token, team.id, m.pseudo));
                    }
                  }}
                >
                  Évincer
                </button>
              )}
            </div>
          </div>
        ))}

        <button
          className="cia-btn cia-btn--danger cia-btn--with-icon"
          style={{ marginTop: 8 }}
          disabled={busy}
          onClick={() => {
            if (window.confirm('Confirmer votre démission de cette équipe ?')) {
              run(async () => { await leaveTeam(token, team.id); onLeft(); });
            }
          }}
        >
          <LogoutIcon size={14} /> Démissionner de l'équipe
        </button>
      </section>
    </div>
  );
}
